/**
 * S1-P6-WORK-QUEUE-ROUTING-SETTINGS-ROLE-ONLY-BUILD-01 - the Work Routing
 * Settings page (`/dashboard/work-routing/settings`).
 *
 * The first product surface that lets authorized operations leaders steer the
 * Work Queue routing rules from the app instead of hand-written SQL: pick a
 * scope (the organization default or one in-scope clinic), a work type, and the
 * role that should receive that work. Role-only this slice - there is NO
 * target-user picker and doctor / patient are never offered.
 *
 * WORK-ROUTING-ADMIN-GOVERNANCE-V1 (Refs #315) turns this from an accreting
 * config list into a governed admin surface (the founder's 2026-07-22 finding:
 * "rules can be added and replaced but never deleted or deactivated; no edit; no
 * record of who set what when; config accretes forever"):
 *   - RULE LIFECYCLE: each rule can be Deactivated / Reactivated via the
 *     existing is_active column (no deletes), behind a confirm that states
 *     exactly what changes; an active rule's role is edited in place through the
 *     existing writer.
 *   - PRECEDENCE MADE VISIBLE: a per-work-type resolution line that renders the
 *     resolver's truth (a clinic's own rule wins, else the organization
 *     default, else unassigned) - no engine change.
 *   - WHO-CHANGED-WHAT: each row shows the last change (actor + role + time)
 *     from the existing audit read, with a compact per-rule history popover.
 *   - SAFETY RAILS: confirm-before-replace when a save shadows a same-scope
 *     rule; the last active rule for a work type cannot be deactivated.
 *
 * ACCESS: reachable only behind RequireRoutingSettings (owner / gm_agm /
 * regional_head). The org-default scope option + org-default writes are
 * owner-only (canEditOrgDefault / canWriteRow), mirroring the backend policy.
 * Backend route auth (canConfigureRouting + the row-level scope policy) remains
 * the real security boundary - this page is UX only.
 *
 * The pure presentational body (WorkRoutingSettingsContent) is rendered directly
 * in tests via react-dom/server (no jsdom), mirroring the WorkRoutingQaPage /
 * LeadsWorkspacePage convention.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getFollowupOwnershipSettings,
  getWorkRoutingSettings,
  setWorkRoutingRuleActive,
  updateFollowupOwnershipSettings,
  updateWorkRoutingSetting,
} from '@/dashboard/api';
import { AcButton } from '@/dashboard/ui';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
import type { DashboardMe } from '@/dashboard/types';
import {
  auditActionLabel,
  auditActorLabel,
  auditForRule,
  auditWhenLabel,
  buildFollowupOwnershipRequest,
  buildUpdateRequest,
  canWriteRow,
  clockBasisLabel,
  deactivateConfirmMessage,
  followupOwnershipSummaryLine,
  isDeactivateBlocked,
  isOrgDefault,
  precedenceGroups,
  reactivateConfirmMessage,
  replaceConfirmMessage,
  roleLabel,
  ROUTING_SETTINGS_EMPTY,
  ROUTING_SETTINGS_INTRO,
  ROUTING_SETTINGS_TITLE,
  routingSaveErrorMessage,
  scopeLabelForConfig,
  scopeOptions,
  shadowedActiveRule,
  templateLabel,
  toggleWindowReadRole,
  type FollowupOwnershipSettingsResponse,
  type RoutingConfigRow,
  type RoutingSettingsResponse,
} from '@/dashboard/work-routing/work-routing-settings-view';

/** A pending confirm descriptor rendered by the content (no closure - pure). */
export interface RoutingConfirmState {
  kind: 'deactivate' | 'reactivate' | 'replace';
  title: string;
  message: string;
  confirmLabel: string;
  tone: 'danger' | 'primary';
}

/** Column headers for the governed rules table (display order). */
const RULE_TABLE_COLUMNS: readonly string[] = [
  'Scope',
  'Work type',
  'Routed to',
  'Status',
  'Last change',
  'Actions',
];

export interface WorkRoutingSettingsContentProps {
  me: DashboardMe | null;
  data: RoutingSettingsResponse;
  scopeValue: string;
  workflowTemplateCode: string;
  targetRole: string;
  onScopeChange?: (value: string) => void;
  onWorkflowChange?: (value: string) => void;
  onTargetRoleChange?: (value: string) => void;
  onSave?: () => void;
  /** A save call is in flight (button disabled). */
  saving: boolean;
  /** A save error message, or null. */
  saveError: string | null;
  /** A save success message, or null. */
  saveSuccess: string | null;

  // --- WORK-ROUTING-ADMIN-GOVERNANCE-V1 (all optional; page supplies live) ---
  /** The rule id whose history popover is open, or null. */
  expandedHistoryRuleId?: number | null;
  onToggleHistory?: (ruleId: number) => void;
  /** The rule id being role-edited in place, or null. */
  editingRuleId?: number | null;
  editRole?: string;
  onStartEdit?: (row: RoutingConfigRow) => void;
  onEditRoleChange?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  /** Request a lifecycle flip (opens the confirm); nextActive=false deactivates. */
  onRequestLifecycle?: (row: RoutingConfigRow, nextActive: boolean) => void;
  /** The pending confirm descriptor, or null. */
  confirm?: RoutingConfirmState | null;
  onConfirmProceed?: () => void;
  onConfirmCancel?: () => void;
  /** A lifecycle / edit write is in flight. */
  lifecycleBusy?: boolean;
  /** A row-level (lifecycle / edit) error message, or null. */
  rowError?: string | null;
}

/** The routable-role <option> list, shared by the form and the inline editor. */
function roleOptions(routableRoles: readonly string[]): JSX.Element[] {
  return routableRoles.map((role) => (
    <option key={role} value={role}>
      {roleLabel(role)}
    </option>
  ));
}

/**
 * Pure presentational body - no fetch, no context. Rendered in tests via
 * react-dom/server. The scope dropdown's options already exclude the org
 * default for a manager (scopeOptions + canEditOrgDefault); the role dropdown
 * lists exactly the server-returned routable roles (never doctor / patient /
 * a specific user).
 */
export function WorkRoutingSettingsContent({
  me,
  data,
  scopeValue,
  workflowTemplateCode,
  targetRole,
  onScopeChange,
  onWorkflowChange,
  onTargetRoleChange,
  onSave,
  saving,
  saveError,
  saveSuccess,
  expandedHistoryRuleId = null,
  onToggleHistory,
  editingRuleId = null,
  editRole = '',
  onStartEdit,
  onEditRoleChange,
  onSaveEdit,
  onCancelEdit,
  onRequestLifecycle,
  confirm = null,
  onConfirmProceed,
  onConfirmCancel,
  lifecycleBusy = false,
  rowError = null,
}: WorkRoutingSettingsContentProps): JSX.Element {
  const options = scopeOptions(me, data.clinics);
  const groups = precedenceGroups(data.configs, data.clinics);

  return (
    <div data-testid="wrs-content">
      {/* --- PRECEDENCE MADE VISIBLE: the resolver's truth, per work type --- */}
      {groups.length > 0 ? (
        <section className="wrs-precedence" data-testid="wrs-precedence">
          <h2>How work routes</h2>
          <p className="wrs-precedence__intro">
            A clinic&rsquo;s own rule wins; otherwise the organization default
            applies; if neither is active, new work of that type is unassigned.
          </p>
          <ul className="wrs-precedence__list">
            {groups.map((g) => (
              <li
                key={g.workflowTemplateCode}
                className="wrs-precedence__item"
                data-testid={`wrs-precedence-${g.workflowTemplateCode}`}
              >
                <span className="wrs-precedence__type">{g.templateLabel}</span>
                <span className="wrs-precedence__chain">{g.resolutionLine}</span>
                {g.clinicRules.length > 0 ? (
                  <span className="wrs-precedence__overrides">
                    Clinic overrides:{' '}
                    {g.clinicRules
                      .map((c) => `${c.clinicLabel} (${roleLabel(c.row.targetRole)})`)
                      .join('; ')}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="wrs-current" data-testid="wrs-current">
        <h2>Current routing rules</h2>

        {rowError !== null ? (
          <p className="wrs-error" role="alert" data-testid="wrs-row-error">
            {rowError}
          </p>
        ) : null}

        {data.configs.length > 0 ? (
          <table className="table wrs-table" data-testid="wrs-rules-table">
            <thead>
              <tr>
                {RULE_TABLE_COLUMNS.map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.configs.map((row) => {
                const writable = canWriteRow(me, row);
                const editing = editingRuleId === row.id;
                const historyOpen = expandedHistoryRuleId === row.id;
                const deactivateBlocked = isDeactivateBlocked(row, data.configs);
                const history = auditForRule(data.audit, row.id);
                return (
                  <RuleRows
                    key={row.id}
                    row={row}
                    clinics={data.clinics}
                    routableRoles={data.routableRoles}
                    writable={writable}
                    editing={editing}
                    editRole={editRole}
                    historyOpen={historyOpen}
                    history={history}
                    deactivateBlocked={deactivateBlocked}
                    lifecycleBusy={lifecycleBusy}
                    onStartEdit={onStartEdit}
                    onEditRoleChange={onEditRoleChange}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    onToggleHistory={onToggleHistory}
                    onRequestLifecycle={onRequestLifecycle}
                  />
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="wrs-empty" data-testid="wrs-empty" role="status">
            {ROUTING_SETTINGS_EMPTY}
          </p>
        )}
      </section>

      <section className="wrs-form" data-testid="wrs-form">
        <h2>Set a routing rule</h2>

        <label className="wrs-field">
          Scope{' '}
          <select
            data-testid="wrs-scope"
            value={scopeValue}
            onChange={(e) => onScopeChange?.(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value === '' ? '__org_default__' : o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="wrs-field">
          Work type{' '}
          <select
            data-testid="wrs-workflow"
            value={workflowTemplateCode}
            onChange={(e) => onWorkflowChange?.(e.target.value)}
          >
            {data.workflowTemplates.map((code) => (
              <option key={code} value={code}>
                {templateLabel(code)}
              </option>
            ))}
          </select>
        </label>

        <label className="wrs-field">
          Routed to{' '}
          <select
            data-testid="wrs-role"
            value={targetRole}
            onChange={(e) => onTargetRoleChange?.(e.target.value)}
          >
            {roleOptions(data.routableRoles)}
          </select>
        </label>

        <button
          type="button"
          className="ac-btn ac-btn--primary wrs-save"
          data-testid="wrs-save"
          disabled={saving}
          onClick={() => onSave?.()}
        >
          {saving ? 'Saving' : 'Save routing rule'}
        </button>

        {saveError !== null ? (
          <p className="wrs-error" role="alert" data-testid="wrs-save-error">
            {saveError}
          </p>
        ) : null}

        {saveSuccess !== null ? (
          <p className="wrs-success" role="status" data-testid="wrs-save-success">
            {saveSuccess}
          </p>
        ) : null}
      </section>

      {/* --- SAFETY RAILS: confirm-before-replace / -deactivate / -reactivate --- */}
      {confirm !== null ? (
        <div className="wrs-confirm" role="dialog" aria-modal="true" data-testid="wrs-confirm">
          <div className="wrs-confirm__card">
            <h3 className="wrs-confirm__title">{confirm.title}</h3>
            <p className="wrs-confirm__message" data-testid="wrs-confirm-message">
              {confirm.message}
            </p>
            <div className="wrs-confirm__actions">
              <button
                type="button"
                className="ac-btn"
                data-testid="wrs-confirm-cancel"
                disabled={lifecycleBusy}
                onClick={() => onConfirmCancel?.()}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirm.tone === 'danger'
                    ? 'ac-btn ac-btn--danger'
                    : 'ac-btn ac-btn--primary'
                }
                data-testid="wrs-confirm-proceed"
                disabled={lifecycleBusy}
                onClick={() => onConfirmProceed?.()}
              >
                {lifecycleBusy ? 'Working' : confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One rule's table row(s): the main row plus, when its history popover is open,
 * a full-width history row beneath it. A component so the main body stays
 * readable; still pure (renders from props, no state).
 */
function RuleRows({
  row,
  clinics,
  routableRoles,
  writable,
  editing,
  editRole,
  historyOpen,
  history,
  deactivateBlocked,
  lifecycleBusy,
  onStartEdit,
  onEditRoleChange,
  onSaveEdit,
  onCancelEdit,
  onToggleHistory,
  onRequestLifecycle,
}: {
  row: RoutingConfigRow;
  clinics: RoutingSettingsResponse['clinics'];
  routableRoles: readonly string[];
  writable: boolean;
  editing: boolean;
  editRole: string;
  historyOpen: boolean;
  history: RoutingSettingsResponse['audit'];
  deactivateBlocked: boolean;
  lifecycleBusy: boolean;
  onStartEdit?: (row: RoutingConfigRow) => void;
  onEditRoleChange?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onToggleHistory?: (ruleId: number) => void;
  onRequestLifecycle?: (row: RoutingConfigRow, nextActive: boolean) => void;
}): JSX.Element {
  const latest = history.length > 0 ? history[0]! : null;
  return (
    <>
      <tr data-testid={`wrs-row-${row.id}`} className={row.isActive ? '' : 'wrs-row--inactive'}>
        <td>{scopeLabelForConfig(row, clinics)}</td>
        <td>{templateLabel(row.workflowTemplateCode)}</td>
        <td>
          {editing ? (
            <span className="wrs-inline-edit">
              <select
                data-testid={`wrs-edit-role-${row.id}`}
                value={editRole}
                onChange={(e) => onEditRoleChange?.(e.target.value)}
              >
                {routableRoles.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ac-btn ac-btn--primary wrs-inline-edit__save"
                data-testid={`wrs-edit-save-${row.id}`}
                disabled={lifecycleBusy}
                onClick={() => onSaveEdit?.()}
              >
                Save
              </button>
              <button
                type="button"
                className="ac-btn wrs-inline-edit__cancel"
                data-testid={`wrs-edit-cancel-${row.id}`}
                disabled={lifecycleBusy}
                onClick={() => onCancelEdit?.()}
              >
                Cancel
              </button>
            </span>
          ) : (
            roleLabel(row.targetRole)
          )}
        </td>
        <td>
          <span
            className={
              row.isActive ? 'badge badge--success' : 'badge badge--muted'
            }
            data-testid={`wrs-status-${row.id}`}
          >
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td>
          <span className="wrs-change" data-testid={`wrs-change-${row.id}`}>
            {latest !== null
              ? `${auditActionLabel(latest)} by ${auditActorLabel(latest)}, ${auditWhenLabel(latest.occurredAt)}`
              : 'No recorded change'}
          </span>
          <button
            type="button"
            className="wrs-link-btn wrs-history-toggle"
            data-testid={`wrs-history-toggle-${row.id}`}
            aria-expanded={historyOpen}
            onClick={() => onToggleHistory?.(row.id)}
          >
            {historyOpen ? 'Hide history' : 'History'}
          </button>
        </td>
        <td>
          <div className="wrs-row-actions">
            {writable && row.isActive && !editing ? (
              <button
                type="button"
                className="wrs-link-btn"
                data-testid={`wrs-edit-${row.id}`}
                onClick={() => onStartEdit?.(row)}
              >
                Edit role
              </button>
            ) : null}
            {writable && row.isActive ? (
              <button
                type="button"
                className="wrs-link-btn wrs-link-btn--danger"
                data-testid={`wrs-deactivate-${row.id}`}
                disabled={deactivateBlocked}
                title={
                  deactivateBlocked
                    ? 'This is the last active rule for this work type; the fallback must always exist.'
                    : undefined
                }
                onClick={() => onRequestLifecycle?.(row, false)}
              >
                Deactivate
              </button>
            ) : null}
            {writable && !row.isActive ? (
              <button
                type="button"
                className="wrs-link-btn"
                data-testid={`wrs-reactivate-${row.id}`}
                onClick={() => onRequestLifecycle?.(row, true)}
              >
                Reactivate
              </button>
            ) : null}
            {deactivateBlocked ? (
              <span
                className="wrs-row-actions__note"
                data-testid={`wrs-lastrule-${row.id}`}
              >
                Last active rule for this work type
              </span>
            ) : null}
          </div>
        </td>
      </tr>
      {historyOpen ? (
        <tr className="wrs-history-row">
          <td colSpan={RULE_TABLE_COLUMNS.length}>
            <div className="wrs-history" data-testid={`wrs-history-${row.id}`}>
              <h4 className="wrs-history__title">
                Change history &mdash; {scopeLabelForConfig(row, clinics)},{' '}
                {templateLabel(row.workflowTemplateCode)}
              </h4>
              {history.length > 0 ? (
                <ul className="wrs-history__list">
                  {history.map((e, i) => (
                    <li key={`${e.entityId}-${i}`} className="wrs-history__entry">
                      <span className="wrs-history__action">
                        {auditActionLabel(e)}
                      </span>
                      <span className="wrs-history__actor">
                        {auditActorLabel(e)}
                      </span>
                      <span className="wrs-history__when">
                        {auditWhenLabel(e.occurredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="wrs-history__empty">
                  No change history recorded for this rule yet.
                </p>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// =====================================================================
// FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 - the Follow-up ownership section:
// WHO owns a follow-up case before/after the boundary, the boundary in
// days, the clock basis, and the window-read visibility grant. The page's
// existing audience reads it; saving follows the page's org-default write
// policy (owner-only), echoed as canEdit -- the backend policy is the
// boundary. All vocabularies are SERVER-RETURNED (no role/day literal
// here). Boundary 0 is a lawful value (the COC-from-day-0 model).
// =====================================================================

export interface FollowupOwnershipSectionContentProps {
  data: FollowupOwnershipSettingsResponse;
  ownerBeforeBoundary: string;
  ownerAfterBoundary: string;
  boundaryDaysText: string;
  clockStartsAt: string;
  windowReadRoles: readonly string[];
  onOwnerBeforeChange?: (value: string) => void;
  onOwnerAfterChange?: (value: string) => void;
  onBoundaryDaysChange?: (value: string) => void;
  onClockChange?: (value: string) => void;
  onToggleWindowReadRole?: (role: string) => void;
  onSave?: () => void;
  saving: boolean;
  saveError: string | null;
  saveSuccess: string | null;
}

/** Pure presentational body of the Follow-up ownership section - rendered
 *  in tests via react-dom/server (the WorkRoutingSettingsContent idiom). */
export function FollowupOwnershipSectionContent({
  data,
  ownerBeforeBoundary,
  ownerAfterBoundary,
  boundaryDaysText,
  clockStartsAt,
  windowReadRoles,
  onOwnerBeforeChange,
  onOwnerAfterChange,
  onBoundaryDaysChange,
  onClockChange,
  onToggleWindowReadRole,
  onSave,
  saving,
  saveError,
  saveSuccess,
}: FollowupOwnershipSectionContentProps): JSX.Element {
  const disabled = !data.canEdit || saving;
  return (
    <section className="wrs-current" data-testid="wrs-followup-ownership">
      <h2>Follow-up ownership</h2>
      <p className="wrs-precedence__intro">
        Who owns patient follow-ups, by case age counted from the{' '}
        {clockBasisLabel(data.values.clockStartsAt).toLowerCase()}. The owner
        before the handoff works cases for their own clinic only; at the
        boundary the case moves to the owner after and leaves the earlier
        queue. Roles granted view-only access can see an in-window case but
        cannot act on it. Set the handoff to 0 to route every case to the
        after-handoff owner from day 0.
      </p>
      <p
        className="wrs-precedence__chain"
        data-testid="wrs-followup-ownership-summary"
      >
        Current rule: {followupOwnershipSummaryLine(data.values)}
      </p>

      <div className="form-grid" data-testid="wrs-followup-ownership-form">
        <label className="form-field">
          <span className="form-field__label">Owner before handoff</span>
          <select
            value={ownerBeforeBoundary}
            disabled={disabled}
            onChange={(e) => onOwnerBeforeChange?.(e.target.value)}
            data-testid="wrs-fo-owner-before"
          >
            {data.ownerRoleOptions.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-field__label">Handoff after (days)</span>
          <input
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={boundaryDaysText}
            disabled={disabled}
            onChange={(e) => onBoundaryDaysChange?.(e.target.value)}
            data-testid="wrs-fo-boundary-days"
          />
        </label>

        <label className="form-field">
          <span className="form-field__label">Owner after handoff</span>
          <select
            value={ownerAfterBoundary}
            disabled={disabled}
            onChange={(e) => onOwnerAfterChange?.(e.target.value)}
            data-testid="wrs-fo-owner-after"
          >
            {data.ownerRoleOptions.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-field__label">Clock starts at</span>
          <select
            value={clockStartsAt}
            disabled={disabled}
            onChange={(e) => onClockChange?.(e.target.value)}
            data-testid="wrs-fo-clock"
          >
            {data.clockBasisOptions.map((basis) => (
              <option key={basis} value={basis}>
                {clockBasisLabel(basis)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Window-read grant: AcButton toggle chips (aria-pressed), the
          design-system multi-select idiom (the follow-ups drawer's item
          picker) -- never a raw native checkbox (conformance RULE 3). */}
      <div
        className="wrs-fo-window-read"
        role="group"
        aria-label="Can see the case during the window (view only)"
        data-testid="wrs-fo-window-read"
      >
        <span className="form-field__label">
          Can see the case during the window (view only)
        </span>
        {data.windowReadRoleOptions.map((role) => (
          <AcButton
            key={role}
            variant={windowReadRoles.includes(role) ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={windowReadRoles.includes(role)}
            disabled={disabled}
            onClick={() => onToggleWindowReadRole?.(role)}
            data-testid={`wrs-fo-windowread-${role}`}
          >
            {roleLabel(role)}
          </AcButton>
        ))}
      </div>

      <div className="wrs-form__actions">
        <button
          type="button"
          className="ac-btn ac-btn--primary"
          disabled={disabled}
          onClick={() => onSave?.()}
          data-testid="wrs-fo-save"
        >
          {saving ? 'Saving...' : 'Save follow-up ownership'}
        </button>
        {!data.canEdit ? (
          <span className="wrs-row-actions__note" data-testid="wrs-fo-readonly">
            Organization-level setting - only the owner can change it. You can
            review the current rule here.
          </span>
        ) : null}
      </div>
      {saveError !== null ? (
        <p className="wrs-error" role="alert" data-testid="wrs-fo-save-error">
          {saveError}
        </p>
      ) : null}
      {saveSuccess !== null ? (
        <p className="wrs-success" data-testid="wrs-fo-save-success">
          {saveSuccess}
        </p>
      ) : null}
    </section>
  );
}

/** Self-fetching, fault-isolated live wrapper for the Follow-up ownership
 *  section (a load fault here never blanks the routing-rules surface). */
export function FollowupOwnershipSection(): JSX.Element | null {
  const [data, setData] = useState<FollowupOwnershipSettingsResponse | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ownerBeforeBoundary, setOwnerBeforeBoundary] = useState('');
  const [ownerAfterBoundary, setOwnerAfterBoundary] = useState('');
  const [boundaryDaysText, setBoundaryDaysText] = useState('');
  const [clockStartsAt, setClockStartsAt] = useState('');
  const [windowReadRoles, setWindowReadRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const adoptResponse = useCallback(
    (res: FollowupOwnershipSettingsResponse) => {
      setData(res);
      setOwnerBeforeBoundary(res.values.ownerBeforeBoundary);
      setOwnerAfterBoundary(res.values.ownerAfterBoundary);
      setBoundaryDaysText(String(res.values.boundaryDays));
      setClockStartsAt(res.values.clockStartsAt);
      setWindowReadRoles([...res.values.windowReadRoles]);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getFollowupOwnershipSettings()
      .then((res) => {
        if (!cancelled) adoptResponse(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error
            ? e.message
            : 'Failed to load follow-up ownership settings',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [adoptResponse]);

  const onSave = useCallback(() => {
    const built = buildFollowupOwnershipRequest({
      ownerBeforeBoundary,
      ownerAfterBoundary,
      boundaryDaysText,
      clockStartsAt,
      windowReadRoles,
    });
    if (!built.ok) {
      setSaveError(built.error);
      setSaveSuccess(null);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    updateFollowupOwnershipSettings(built.request)
      .then((res) => {
        adoptResponse(res);
        setSaveSuccess('Follow-up ownership saved.');
      })
      .catch((e: unknown) => {
        setSaveError(routingSaveErrorMessage(e));
      })
      .finally(() => setSaving(false));
  }, [
    adoptResponse,
    ownerBeforeBoundary,
    ownerAfterBoundary,
    boundaryDaysText,
    clockStartsAt,
    windowReadRoles,
  ]);

  if (loadError !== null) {
    return (
      <section className="wrs-current" data-testid="wrs-followup-ownership-error">
        <h2>Follow-up ownership</h2>
        <p className="wrs-error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }
  if (data === null) {
    return (
      <section className="wrs-current" data-testid="wrs-followup-ownership-loading">
        <h2>Follow-up ownership</h2>
        <div className="loading">Loading</div>
      </section>
    );
  }
  return (
    <FollowupOwnershipSectionContent
      data={data}
      ownerBeforeBoundary={ownerBeforeBoundary}
      ownerAfterBoundary={ownerAfterBoundary}
      boundaryDaysText={boundaryDaysText}
      clockStartsAt={clockStartsAt}
      windowReadRoles={windowReadRoles}
      onOwnerBeforeChange={setOwnerBeforeBoundary}
      onOwnerAfterChange={setOwnerAfterBoundary}
      onBoundaryDaysChange={(v) => {
        setBoundaryDaysText(v);
        setSaveError(null);
        setSaveSuccess(null);
      }}
      onClockChange={setClockStartsAt}
      onToggleWindowReadRole={(role) =>
        setWindowReadRoles((cur) => toggleWindowReadRole(cur, role))
      }
      onSave={onSave}
      saving={saving}
      saveError={saveError}
      saveSuccess={saveSuccess}
    />
  );
}

/**
 * Self-fetching, fault-isolated settings page. Reachable only behind the
 * RequireRoutingSettings role guard.
 */
export default function WorkRoutingSettingsPage(): JSX.Element {
  const ctx = useDashboardMeContext();
  const me = ctx?.me ?? null;

  const [data, setData] = useState<RoutingSettingsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scopeValue, setScopeValue] = useState<string>('');
  const [workflowTemplateCode, setWorkflowTemplateCode] =
    useState<string>('new_lead_followup');
  const [targetRole, setTargetRole] = useState<string>('receptionist');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Governance state (WORK-ROUTING-ADMIN-GOVERNANCE-V1).
  const [expandedHistoryRuleId, setExpandedHistoryRuleId] = useState<number | null>(
    null,
  );
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editRole, setEditRole] = useState<string>('');
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    descriptor: RoutingConfirmState;
    run: () => void;
  } | null>(null);

  const refresh = useCallback(
    () => getWorkRoutingSettings().then((res) => setData(res)),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getWorkRoutingSettings()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        // Default the scope picker to the first allowed option (org default for
        // the owner, the first in-scope clinic for a manager).
        const opts = scopeOptions(me, res.clinics);
        setScopeValue(opts.length > 0 ? opts[0]!.value : '');
        if (res.workflowTemplates.length > 0) {
          setWorkflowTemplateCode(res.workflowTemplates[0]!);
        }
        if (res.routableRoles.length > 0) {
          setTargetRole(res.routableRoles[0]!);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : 'Failed to load routing settings',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [me]);

  const doSave = useCallback(() => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    const request = buildUpdateRequest(
      scopeValue,
      workflowTemplateCode,
      targetRole,
    );
    updateWorkRoutingSetting(request)
      .then((result) => {
        setSaveSuccess(
          result.outcome === 'created'
            ? 'Routing rule created.'
            : 'Routing rule updated.',
        );
        return refresh();
      })
      .catch((e: unknown) => {
        setSaveError(routingSaveErrorMessage(e));
      })
      .finally(() => setSaving(false));
  }, [scopeValue, workflowTemplateCode, targetRole, refresh]);

  // SAFETY RAIL: confirm-before-replace when the save shadows a same-scope rule.
  const onSave = useCallback(() => {
    if (data === null) return;
    const shadowed = shadowedActiveRule(
      scopeValue,
      workflowTemplateCode,
      data.configs,
    );
    if (shadowed !== null) {
      setPending({
        descriptor: {
          kind: 'replace',
          title: 'Replace the existing rule?',
          message: replaceConfirmMessage(shadowed, targetRole, data.clinics),
          confirmLabel: 'Replace rule',
          tone: 'primary',
        },
        run: () => {
          setPending(null);
          doSave();
        },
      });
      return;
    }
    doSave();
  }, [data, scopeValue, workflowTemplateCode, targetRole, doSave]);

  const onToggleHistory = useCallback((ruleId: number) => {
    setExpandedHistoryRuleId((prev) => (prev === ruleId ? null : ruleId));
  }, []);

  const onStartEdit = useCallback((row: RoutingConfigRow) => {
    setRowError(null);
    setEditingRuleId(row.id);
    setEditRole(row.targetRole);
  }, []);

  const onCancelEdit = useCallback(() => {
    setEditingRuleId(null);
    setEditRole('');
  }, []);

  // Edit-in-place for target_role via the EXISTING writer: the PUT updates the
  // single active rule for this scope + work type (this row) in place.
  const onSaveEdit = useCallback(() => {
    if (data === null || editingRuleId === null) return;
    const row = data.configs.find((r) => r.id === editingRuleId);
    if (row === undefined) return;
    setLifecycleBusy(true);
    setRowError(null);
    const request = buildUpdateRequest(
      isOrgDefault(row) ? '' : String(row.clinicId),
      row.workflowTemplateCode,
      editRole,
    );
    updateWorkRoutingSetting(request)
      .then(() => {
        setEditingRuleId(null);
        setEditRole('');
        return refresh();
      })
      .catch((e: unknown) => {
        setRowError(routingSaveErrorMessage(e));
      })
      .finally(() => setLifecycleBusy(false));
  }, [data, editingRuleId, editRole, refresh]);

  const doLifecycle = useCallback(
    (ruleId: number, nextActive: boolean) => {
      setLifecycleBusy(true);
      setRowError(null);
      setWorkRoutingRuleActive(ruleId, nextActive)
        .then(() => {
          setPending(null);
          return refresh();
        })
        .catch((e: unknown) => {
          setPending(null);
          setRowError(routingSaveErrorMessage(e));
        })
        .finally(() => setLifecycleBusy(false));
    },
    [refresh],
  );

  const onRequestLifecycle = useCallback(
    (row: RoutingConfigRow, nextActive: boolean) => {
      if (data === null) return;
      setRowError(null);
      const descriptor: RoutingConfirmState = nextActive
        ? {
            kind: 'reactivate',
            title: 'Reactivate this rule?',
            message: reactivateConfirmMessage(row, data.clinics),
            confirmLabel: 'Reactivate',
            tone: 'primary',
          }
        : {
            kind: 'deactivate',
            title: 'Deactivate this rule?',
            message: deactivateConfirmMessage(row, data.configs, data.clinics),
            confirmLabel: 'Deactivate',
            tone: 'danger',
          };
      setPending({ descriptor, run: () => doLifecycle(row.id, nextActive) });
    },
    [data, doLifecycle],
  );

  return (
    <section className="wrs">
      <div className="page-header">
        <h1>{ROUTING_SETTINGS_TITLE}</h1>
        <p className="page-header__sub">{ROUTING_SETTINGS_INTRO}</p>
      </div>

      {/*
        OWNER-OPERATING-SYSTEM-V1 (tenancy audit row 27, REMOVE): the
        "Staff management" CTA and its 11-line scaffold stub are retired --
        a live door into an empty room (SEAM law). Staff management returns
        with a real roster/shift package (the staff-attendance spine recon
        stays a founder-pending board item); until then no surface
        advertises it.
      */}

      {ctx?.loading ? (
        <div className="loading">Loading</div>
      ) : loadError !== null ? (
        <DashboardLoadError message={loadError} />
      ) : data === null ? (
        <div className="loading">Loading</div>
      ) : (
        <WorkRoutingSettingsContent
          me={me}
          data={data}
          scopeValue={scopeValue}
          workflowTemplateCode={workflowTemplateCode}
          targetRole={targetRole}
          onScopeChange={setScopeValue}
          onWorkflowChange={setWorkflowTemplateCode}
          onTargetRoleChange={setTargetRole}
          onSave={onSave}
          saving={saving}
          saveError={saveError}
          saveSuccess={saveSuccess}
          expandedHistoryRuleId={expandedHistoryRuleId}
          onToggleHistory={onToggleHistory}
          editingRuleId={editingRuleId}
          editRole={editRole}
          onStartEdit={onStartEdit}
          onEditRoleChange={setEditRole}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          onRequestLifecycle={onRequestLifecycle}
          confirm={pending?.descriptor ?? null}
          onConfirmProceed={() => pending?.run()}
          onConfirmCancel={() => setPending(null)}
          lifecycleBusy={lifecycleBusy}
          rowError={rowError}
        />
      )}

      {/* FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1: the Follow-up ownership
          section - self-fetching and fault-isolated, so a fault on either
          surface never blanks the other. */}
      {!ctx?.loading ? <FollowupOwnershipSection /> : null}
    </section>
  );
}
